import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { loadTakiConfig, type TakiConfig } from '@taki/runtime-config';
import { createTakiSdk, type TakiSdk } from '@taki/sdk';
import { initAuth } from '@taki/auth';
import { App } from './App.js';
import './index.css';

/**
 * Point d'entree de l'app `hello`.
 *
 * Sequence de boot (ordre IMPOSE par l'archi MyTaki) :
 *   1. loadTakiConfig()  -> lit `/taki.config.json` au RUNTIME (rien en dur).
 *   2. createTakiSdk(cfg) -> initialise Firebase + Firestore, expose les repos.
 *   3. initAuth(sdk.app)  -> branche Firebase Auth sur la MEME app Firebase.
 *   4. rendu React, config + sdk passes en props (frontiere de config).
 *
 * Si l'etape 1 echoue (fichier absent / invalide), on affiche un ecran d'erreur
 * explicite plutot qu'un ecran blanc : c'est le cas "config client manquante".
 */

function mountFatalError(message: string, detail?: string): void {
  const root = document.getElementById('root');
  if (!root) return;
  root.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.setAttribute(
    'style',
    'max-width:560px;margin:10vh auto;padding:24px;font-family:system-ui,sans-serif;line-height:1.5;color:#0f172a;',
  );

  const title = document.createElement('h1');
  title.textContent = 'Configuration MyTaki introuvable';
  title.setAttribute('style', 'font-size:18px;margin:0 0 8px;');

  const body = document.createElement('p');
  body.textContent = message;
  body.setAttribute('style', 'margin:0 0 12px;');

  wrapper.append(title, body);

  if (detail) {
    const pre = document.createElement('pre');
    pre.textContent = detail;
    pre.setAttribute(
      'style',
      'background:#f1f5f9;border-radius:8px;padding:12px;font-size:12px;overflow:auto;white-space:pre-wrap;',
    );
    wrapper.append(pre);
  }

  const hint = document.createElement('p');
  hint.setAttribute('style', 'margin:12px 0 0;font-size:13px;opacity:0.7;');
  hint.textContent =
    'Verifie public/taki.config.json (firebaseConfig de ton projet Firebase de test).';
  wrapper.append(hint);

  root.append(wrapper);
}

async function boot(): Promise<void> {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error("Element racine #root introuvable dans index.html.");
  }

  let config: TakiConfig;
  try {
    // 1. Config chargee au RUNTIME (avant tout init Firebase).
    config = await loadTakiConfig();
  } catch (error) {
    mountFatalError(
      "Impossible de charger la configuration au demarrage.",
      error instanceof Error ? error.message : String(error),
    );
    return;
  }

  // 2. SDK : init Firebase + Firestore depuis la config runtime (y compris le
  //    branchement sur l'emulateur Firestore si la config porte `emulators`).
  const sdk: TakiSdk = createTakiSdk(config);

  // 3. Auth : branchee sur la MEME app Firebase que le SDK, et sur l'emulateur
  //    Auth local quand la config le demande (dev 100% local, `pnpm dev:local`).
  initAuth(sdk.app, config.emulators?.auth);

  // 4. Rendu React.
  createRoot(rootElement).render(
    <StrictMode>
      <App config={config} sdk={sdk} />
    </StrictMode>,
  );
}

void boot();
